import config from '../../config/config';

import deleteRequest from './base/delete';
import getRequest from './base/get';
import postRequest from './base/post';
import putRequest from './base/put';

/**
 * OCPI API service for management operations.
 */
const ocpiApi = {
  /**
   * List all registered OCPI connections.
   * @param {string} role - Tenant role ('CPO' | 'EMSP') to filter connections by.
   * @param {string} [tenantId] - Tenant id to scope connections to a specific role instance.
   */
  listConnections: (role: string, tenantId?: string): Promise<any> => {
    const queryParameters = new URLSearchParams();
    if (role) queryParameters.set('role', role);
    if (tenantId) queryParameters.set('tenantId', tenantId);
    const query = queryParameters.toString() ? `?${queryParameters.toString()}` : '';
    return getRequest(`${config.ocpiApiUrl}internal/credentials/connections${query}`);
  },

  /**
   * Get CPO EMSP connections for populating EMSP partners list.
   * @returns {Promise<any>} The response containing CPO info and EMSP connections list.
   */
  getCpoEmspConnections: (): Promise<any> => {
    return getRequest(`${config.ocpiApiUrl}internal/credentials/cpo-emsp-connections?role=CPO`);
  },

  /**
   * List all registered OCPI tenants (internal identities).
   */
  listTenants: (): Promise<any> => {
    return getRequest(`${config.ocpiApiUrl}internal/tenant`);
  },

  /**
   * Get the OCPI version list cached on the connection between our tenant (resolved
   * server-side from the auth token) and a given counterparty.
   * @param {any} role - Our own tenant role ('CPO' | 'EMSP'), also used by the auth
   * middleware to resolve our tenant from the token.
   * @param {any} counterPartyId - The counterparty's party ID.
   * @param {any} counterCountryCode - The counterparty's country code.
   * @param {any} counterRole - The counterparty's role ('CPO' | 'EMSP').
   */
  getInternalConnectionVersions: (
    role: string,
    counterPartyId: string,
    counterCountryCode: string,
    counterRole: string,
  ): Promise<any> => {
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/connections/versions?role=${role}&counterPartyId=${counterPartyId}&counterCountryCode=${counterCountryCode}&counterRole=${counterRole}`,
    );
  },

  /**
   * Create a new OCPI tenant (activate roaming).
   * @param {any} data { partyId, countryCode, role, businessDetails }
   */
  createTenant: (data: any): Promise<any> => {
    return postRequest(`${config.ocpiApiUrl}internal/tenant`, data);
  },

  /**
   * Get our own platform OCPI credentials.
   */
  getOurCredentials: (): Promise<any> => {
    return getRequest(`${config.ocpiApiUrl}internal/credentials`);
  },

  /**
   * Register as a Sender (handshake with a partner).
   * @param {any} data { theirTokenA, theirVersionsUrl, ourPartyId, counterPartyId, counterCountryCode, counterRole }
   */
  registerAsSender: (data: any): Promise<any> => {
    return postRequest(`${config.ocpiApiUrl}internal/credentials/register-as-sender`, data);
  },

  /**
   * Generate Token A for an incoming partner.
   * @param {any} data { ourPartyId, ourRole, counterPartyId, counterCountryCode, counterRole }
   */
  generateTokenA: (data: any): Promise<any> => {
    return postRequest(`${config.ocpiApiUrl}internal/credentials/generate-token-a`, data);
  },

  /**
   * Terminate a connection (unregister as sender), removing the roaming link with a partner.
   * @param {string} role - Our own tenant role ('CPO' | 'EMSP') sending the unregister request.
   * @param {any} data - { counterPartyId, counterCountryCode, counterRole } of the partner to disconnect from.
   */
  deleteConnection: (role: string, data: any): Promise<any> => {
    return postRequest(
      `${config.ocpiApiUrl}internal/credentials/unregister-as-sender?role=${role}`,
      data,
    );
  },

  /**
   * Suspend a connection (unregister as sender), removing the roaming link with a partner.
   * @param {string} id - The connection ID.
   */
  suspendConnection: (id: string, role: string): Promise<any> => {
    return deleteRequest(
      `${config.ocpiApiUrl}internal/credentials/connections/suspend/${id}?role=${role}`,
    );
  },

  /**
   * Reconnect a connection (unregister as sender), removing the roaming link with a partner.
   * @param {string} id - The connection ID.
   */
  reconnectConnection: (id: string, role: string): Promise<any> => {
    return putRequest(
      `${config.ocpiApiUrl}internal/credentials/connections/reconnect/${id}?role=${role}`,
      {},
    );
  },

  /**
   * Unregister a connection (unregister as sender), removing the roaming link with a partner.
   * @param {string} id - The connection ID.
   */
  unregisterConnection: (id: string, role: string): Promise<any> => {
    return deleteRequest(
      `${config.ocpiApiUrl}internal/credentials/connections/unregister/${id}?role=${role}`,
    );
  },

  /**
   * Delete an OCPI tenant (role).
   * @param {any} id - The id
   */
  deleteTenant: (id: string): Promise<any> => {
    return deleteRequest(`${config.ocpiApiUrl}internal/tenant/${id}`);
  },

  /**
   * Authorize a connection for receiver role.
   * @param {any} id - The id
   */
  authorizeConnection: (id: string): Promise<any> => {
    return postRequest(`${config.ocpiApiUrl}internal/credentials/connections/${id}/authorize`, {});
  },

  /**
   * Internal Operations
   * @param {any} countryCode - The countryCode
   * @param {any} partyId - The partyId
   * @param {any} page - The page
   * @param {any} limit - The limit
   */
  getInternalCpoLocations: (
    countryCode: string,
    partyId: string,
    page = 1,
    limit = 50,
  ): Promise<any> => {
    const offset = (page - 1) * limit;
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/cpo/${countryCode}/${partyId}/locations?limit=${limit}&offset=${offset}&role=CPO`,
    );
  },

  /**
   * Get locations received from connected CPOs for this EMSP role.
   * @param {any} countryCode - The countryCode
   * @param {any} partyId - The partyId
   * @param {any} counterPartyId - The partner CPO's partyId to filter by
   * @param {any} counterCountryCode - The partner CPO's countryCode to filter by
   * @param {any} page - The page
   * @param {any} limit - The limit
   */
  getInternalEmspLocations: (
    countryCode: string,
    partyId: string,
    counterPartyId: string,
    counterCountryCode: string,
    page = 1,
    limit = 50,
  ): Promise<any> => {
    const offset = (page - 1) * limit;
    const queryParameters = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      role: 'EMSP',
    });
    if (counterPartyId) queryParameters.set('counterPartyId', counterPartyId);
    if (counterCountryCode) queryParameters.set('counterCountryCode', counterCountryCode);
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/emsp/${countryCode}/${partyId}/locations?${queryParameters.toString()}`,
    );
  },

  /**
   * Get tariffs received from connected CPOs for this EMSP role.
   * @param {any} countryCode - The countryCode
   * @param {any} partyId - The partyId
   * @param {any} counterPartyId - The partner CPO's partyId to filter by
   * @param {any} counterCountryCode - The partner CPO's countryCode to filter by
   * @param {any} page - The page
   * @param {any} limit - The limit
   */
  getInternalEmspTariffs: (
    countryCode: string,
    partyId: string,
    counterPartyId: string,
    counterCountryCode: string,
    page = 1,
    limit = 50,
  ): Promise<any> => {
    const offset = (page - 1) * limit;
    const queryParameters = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      role: 'EMSP',
    });
    if (counterPartyId) queryParameters.set('counterPartyId', counterPartyId);
    if (counterCountryCode) queryParameters.set('counterCountryCode', counterCountryCode);
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/emsp/${countryCode}/${partyId}/tariffs?${queryParameters.toString()}`,
    );
  },

  /**
   * Pull CPO locations manually from a connected partner CPO.
   * @param {any} countryCode - The countryCode
   * @param {any} partyId - The partyId
   * @param {any} counterPartyId - The partner CPO's partyId
   * @param {any} counterCountryCode - The partner CPO's countryCode
   * @param {any} connectionId - The connectionId
   */
  pullInternalEmspLocations: (
    countryCode: string,
    partyId: string,
    counterPartyId?: string,
    counterCountryCode?: string,
    connectionId?: string,
  ): Promise<any> => {
    return postRequest(
      `${config.ocpiApiUrl}internal/operations/emsp/${countryCode}/${partyId}/locations/pull?role=EMSP&counterPartyId=${
        counterPartyId || ''
      }&counterCountryCode=${counterCountryCode || ''}`,
      { connectionId },
    );
  },

  /**
   * Pull CPO tariffs manually from a connected partner CPO.
   * @param {any} countryCode - The countryCode
   * @param {any} partyId - The partyId
   * @param {any} counterPartyId - The partner CPO's partyId
   * @param {any} counterCountryCode - The partner CPO's countryCode
   * @param {any} connectionId - The connectionId
   */
  pullInternalEmspTariffs: (
    countryCode: string,
    partyId: string,
    counterPartyId?: string,
    counterCountryCode?: string,
    connectionId?: string,
  ): Promise<any> => {
    return postRequest(
      `${config.ocpiApiUrl}internal/operations/emsp/${countryCode}/${partyId}/tariffs/pull?role=EMSP&counterPartyId=${
        counterPartyId || ''
      }&counterCountryCode=${counterCountryCode || ''}`,
      { connectionId },
    );
  },

  /**
   * Synchronizes CPO locations from the CPO backend.
   * @param {any} countryCode The country code.
   * @param {any} partyId The party ID.
   * @returns {any} A promise of the API response.
   */
  syncInternalCpoLocations: (countryCode: string, partyId: string): Promise<any> => {
    return postRequest(
      `${config.ocpiApiUrl}internal/operations/cpo/${countryCode}/${partyId}/locations/sync?role=CPO`,
      {},
    );
  },

  /**
   * Broadcasts CPO locations to connected partners.
   * @param {any} countryCode The country code.
   * @param {any} partyId The party ID.
   * @returns {any} A promise of the API response.
   */
  broadcastInternalCpoLocations: (countryCode: string, partyId: string): Promise<any> => {
    return postRequest(
      `${config.ocpiApiUrl}internal/operations/cpo/${countryCode}/${partyId}/locations/broadcast?role=CPO`,
      {},
    );
  },

  /**
   * Manually push a specific location (with selected EVSEs) to a partner connection.
   * @param {any} countryCode - The countryCode
   * @param {any} partyId - The partyId
   * @param {any} locationId - The locationId
   * @param {any} data - The data
   * @param {any} data.connectionId - The connectionId prop
   * @param {any} data.evseUids - The evseUids prop
   */
  pushLocation: (
    countryCode: string,
    partyId: string,
    locationId: string,
    data: { connectionId: string; evseUids: string[] },
  ): Promise<any> => {
    return postRequest(
      `${config.ocpiApiUrl}internal/operations/cpo/${countryCode}/${partyId}/locations/${locationId}/push?role=CPO`,
      data,
    );
  },

  /**
   * List additional-field configuration for all modules of a tenant (role).
   * @param {any} role - Tenant role ('CPO' | 'EMSP'); selects the path segment.
   * @param {any} countryCode - The country code.
   * @param {any} partyId - The party ID.
   */
  getAdditionalFields: (role: string, countryCode: string, partyId: string): Promise<any> => {
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/${role.toLowerCase()}/${countryCode}/${partyId}/additional-fields?role=${role.toUpperCase()}`,
    );
  },

  /**
   * Update additional-field configuration for a single module of a tenant (role).
   * @param {any} role - Tenant role ('CPO' | 'EMSP'); selects the path segment.
   * @param {any} countryCode - The country code.
   * @param {any} partyId - The party ID.
   * @param {any} module - The module name (e.g. 'sessions').
   * @param {any} fields - Array of { name, enabled?, alias? }.
   */
  updateAdditionalFields: (
    role: string,
    countryCode: string,
    partyId: string,
    module: string,
    fields: { name: string; enabled?: boolean; alias?: string }[],
  ): Promise<any> => {
    return putRequest(
      `${config.ocpiApiUrl}internal/operations/${role.toLowerCase()}/${countryCode}/${partyId}/additional-fields/${module}?role=${role.toUpperCase()}`,
      { fields },
    );
  },

  /**
   * Get OCPI tariffs published for a CPO role.
   * @param {any} countryCode The country code.
   * @param {any} partyId The party ID.
   * @param {any} page The page.
   * @param {any} limit The limit.
   * @returns {any} A promise of the API response.
   */
  getInternalCpoTariffs: (
    countryCode: string,
    partyId: string,
    page = 1,
    limit = 50,
  ): Promise<any> => {
    const offset = (page - 1) * limit;
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/cpo/${countryCode}/${partyId}/tariffs?limit=${limit}&offset=${offset}&role=CPO`,
    );
  },

  /**
   * Broadcasts CPO tariffs to connected partners.
   * @param {any} countryCode The country code.
   * @param {any} partyId The party ID.
   * @returns {any} A promise of the API response.
   */
  broadcastInternalCpoTariffs: (countryCode: string, partyId: string): Promise<any> => {
    return postRequest(
      `${config.ocpiApiUrl}internal/operations/cpo/${countryCode}/${partyId}/tariffs/broadcast?role=CPO`,
    );
  },

  /**
   * Get OCPI charging sessions for a CPO role.
   * @param {any} countryCode The country code.
   * @param {any} partyId The party ID.
   * @param {string} [counterPartyId] The counterparty ID.
   * @param {string} [counterCountryCode] The counterparty country code.
   * @param {any} page The page.
   * @param {any} limit The limit.
   * @returns {any} A promise of the API response.
   */
  getInternalCpoSessions: (
    countryCode: string,
    partyId: string,
    counterPartyId: string,
    counterCountryCode: string,
    page = 1,
    limit = 50,
  ): Promise<any> => {
    const offset = (page - 1) * limit;
    const queryParameters = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      role: 'CPO',
    });
    if (counterPartyId) queryParameters.set('counterPartyId', counterPartyId);
    if (counterCountryCode) queryParameters.set('counterCountryCode', counterCountryCode);
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/cpo/${countryCode}/${partyId}/sessions?${queryParameters.toString()}`,
    );
  },

  /**
   * Broadcasts CPO charging sessions to connected partners.
   * @param {any} countryCode The country code.
   * @param {any} partyId The party ID.
   * @returns {any} A promise of the API response.
   */
  broadcastInternalCpoSessions: (countryCode: string, partyId: string): Promise<any> => {
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/cpo/${countryCode}/${partyId}/sessions/broadcast?role=CPO`,
    );
  },

  /**
   * Get charging sessions received from connected CPOs for this EMSP role.
   * @param {any} countryCode - The countryCode
   * @param {any} partyId - The partyId
   * @param {any} counterPartyId - The partner CPO's partyId to filter by
   * @param {any} counterCountryId - The partner CPO's countryCode to filter by
   * @param {any} page - The page
   * @param {any} limit - The limit
   */
  getInternalEmspSessions: (
    countryCode: string,
    partyId: string,
    counterPartyId: string,
    counterCountryId: string,
    page = 1,
    limit = 50,
  ): Promise<any> => {
    const offset = (page - 1) * limit;
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/emsp/${countryCode}/${partyId}/sessions?limit=${limit}&offset=${offset}&role=EMSP&counterPartyId=${counterPartyId}&counterCountryCode=${counterCountryId}`,
    );
  },

  /**
   * Get OCPI tokens received from connected eMSPs for a CPO role.
   * @param {any} countryCode - The countryCode
   * @param {any} partyId - The partyId
   * @param {any} counterPartyId - The partner eMSP's partyId to filter by
   * @param {any} counterCountryId - The partner eMSP's countryCode to filter by
   * @param {any} page - The page
   * @param {any} limit - The limit
   */
  getInternalCpoTokens: (
    countryCode: string,
    partyId: string,
    counterPartyId: string,
    counterCountryId: string,
    page = 1,
    limit = 50,
  ): Promise<any> => {
    const offset = (page - 1) * limit;
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/cpo/${countryCode}/${partyId}/tokens?limit=${limit}&offset=${offset}&role=CPO&counterPartyId=${counterPartyId}&counterCountryCode=${counterCountryId}`,
    );
  },

  /**
   * Get OCPI commands received from connected eMSPs for a CPO role.
   * @param {any} countryCode - The countryCode
   * @param {any} partyId - The partyId
   * @param {any} counterPartyId - The partner eMSP's partyId to filter by
   * @param {any} counterCountryId - The partner eMSP's countryCode to filter by
   * @param {any} page - The page
   * @param {any} limit - The limit
   */
  getInternalCpoCommands: (
    countryCode: string,
    partyId: string,
    counterPartyId: string,
    counterCountryId: string,
    page = 1,
    limit = 50,
  ): Promise<any> => {
    const offset = (page - 1) * limit;
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/cpo/${countryCode}/${partyId}/commands?limit=${limit}&offset=${offset}&role=CPO&counterPartyId=${counterPartyId}&counterCountryCode=${counterCountryId}`,
    );
  },

  /**
   * Get OCPI connection logs received from connected eMSPs for a CPO role.
   * @param {any} connectionId - The partner eMSP's countryCode to filter by
   * @param {any} page - The page
   * @param {any} limit - The limit
   */
  getInternalCpoConnectionLog: (request: any): Promise<any> => {
    const { connectionId, ...data } = request;
    return getRequest(
      `${config.ocpiApiUrl}internal/credentials/activity-logs/${connectionId}`,
      data,
    );
  },

  /**
   * Get OCPI connection logs received from connected eMSPs for a CPO role.
   * @param {any} connectionId - The partner eMSP's countryCode to filter by
   * @param {any} page - The page
   * @param {any} limit - The limit
   */
  getInternalCpoConnectionLogFilter: (request: any): Promise<any> => {
    return getRequest(`${config.ocpiApiUrl}internal/credentials/activity-logs/filter-list`, {
      ...request,
    });
  },

  /**
   * Get OCPI connection logs received from connected eMSPs for a CPO role.
   * @param {any} connectionId - The partner eMSP's countryCode to filter by
   * @param {any} page - The page
   * @param {any} limit - The limit
   */
  getInternalCpoConnectionLogExport: (request: any): Promise<any> => {
    const { connectionId, ...data } = request;
    return getRequest(
      `${config.ocpiApiUrl}internal/credentials/activity-logs/${connectionId}/export`,
      { ...data },
    );
  },

  /**
   * Get OCPI CDRs (charge detail records) for a CPO role.
   * @param {any} countryCode The country code.
   * @param {any} partyId The party ID.
   * @param {string} [counterPartyId] The counterparty ID.
   * @param {string} [counterCountryCode] The counterparty country code.
   * @param {any} page The page.
   * @param {any} limit The limit.
   * @returns {any} A promise of the API response.
   */
  getInternalCpoCdrs: (
    countryCode: string,
    partyId: string,
    counterPartyId: string,
    counterCountryCode: string,
    page = 1,
    limit = 50,
  ): Promise<any> => {
    const offset = (page - 1) * limit;
    const queryParameters = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      role: 'CPO',
    });
    if (counterPartyId) queryParameters.set('counterPartyId', counterPartyId);
    if (counterCountryCode) queryParameters.set('counterCountryCode', counterCountryCode);
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/cpo/${countryCode}/${partyId}/cdrs?${queryParameters.toString()}`,
    );
  },

  /**
   * Broadcasts CPO CDRs to connected partners.
   * @param {any} countryCode The country code.
   * @param {any} partyId The party ID.
   * @returns {any} A promise of the API response.
   */
  broadcastInternalCpoCdrs: (countryCode: string, partyId: string): Promise<any> => {
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/cpo/${countryCode}/${partyId}/cdrs/broadcast?role=CPO`,
    );
  },

  /**
   * Get CDRs received from connected CPOs for this EMSP role.
   * @param {any} countryCode - The countryCode
   * @param {any} partyId - The partyId
   * @param {any} counterPartyId - The partner CPO's partyId to filter by
   * @param {any} counterCountryId - The partner CPO's countryCode to filter by
   * @param {any} page - The page
   * @param {any} limit - The limit
   */
  getInternalEmspCdrs: (
    countryCode: string,
    partyId: string,
    counterPartyId: string,
    counterCountryId: string,
    page = 1,
    limit = 50,
  ): Promise<any> => {
    const offset = (page - 1) * limit;
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/emsp/${countryCode}/${partyId}/cdrs?limit=${limit}&offset=${offset}&role=EMSP&counterPartyId=${counterPartyId}&counterCountryCode=${counterCountryId}`,
    );
  },
  /**
   * Get OCPI tokens owned by an EMSP role.
   * @param {any} countryCode - The countryCode
   * @param {any} partyId - The partyId
   * @param {any} counterPartyId - The partner CPO's partyId to filter by
   * @param {any} counterCountryId - The partner CPO's countryCode to filter by
   * @param {any} page - The page
   * @param {any} limit - The limit
   */
  getInternalEmspTokens: (
    countryCode: string,
    partyId: string,
    counterPartyId: string,
    counterCountryId: string,
    page = 1,
    limit = 50,
  ): Promise<any> => {
    const offset = (page - 1) * limit;
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/emsp/${countryCode}/${partyId}/tokens?limit=${limit}&offset=${offset}&role=EMSP&counterPartyId=${counterPartyId}&counterCountryCode=${counterCountryId}`,
    );
  },

  /**
   * Broadcasts EMSP tokens to connected partners.
   * @param {any} countryCode The country code.
   * @param {any} partyId The party ID.
   * @returns {any} A promise of the API response.
   */
  broadcastInternalEmspTokens: (countryCode: string, partyId: string): Promise<any> => {
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/emsp/${countryCode}/${partyId}/tokens/broadcast?role=EMSP`,
    );
  },

  /**
   * Get OCPI commands for an EMSP role.
   * @param {any} countryCode - The countryCode
   * @param {any} partyId - The partyId
   * @param {any} counterPartyId - The partner CPO's partyId to filter by
   * @param {any} counterCountryId - The partner CPO's countryCode to filter by
   * @param {any} page - The page
   * @param {any} limit - The limit
   */
  getInternalEmspCommands: (
    countryCode: string,
    partyId: string,
    counterPartyId: string,
    counterCountryId: string,
    page = 1,
    limit = 50,
  ): Promise<any> => {
    const offset = (page - 1) * limit;
    return getRequest(
      `${config.ocpiApiUrl}internal/operations/emsp/${countryCode}/${partyId}/commands?limit=${limit}&offset=${offset}&role=EMSP&counterPartyId=${counterPartyId}&counterCountryCode=${counterCountryId}`,
    );
  },
};

export default ocpiApi;
